import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { FormProvider } from 'react-hook-form';
import { isEqual } from 'lodash';
import { FormProps, FormType } from './types';
import { SchemaProvider } from './contexts/useSchema';
import InputNumber from './components/input-number';
import ErrorMessage from './components/error-message';
import InputText from './components/input-text';
import InputTextarea from './components/input-text-area';
import Checkbox from './components/checkbox';
import Dropdown from './components/dropdown';
import Calendar from './components/calendar';
import ColorPicker from './components/color-picker';
import MultiSelect from './components/multi-select';

const Form = <T extends Record<string, any>>({
  children,
  formObject,
  onSubmit,
  submitByEnter = true,
  className,
}: PropsWithChildren<FormProps<T>>) => {
  const { form, context, schema } = formObject;

  const getSchemaDescription = useCallback(
    (value: T) => schema.describe({ value, context }),
    [context, schema],
  );

  const [schemaDescription, setSchemaDescription] = useState(() =>
    getSchemaDescription(form.getValues()),
  );

  const preventSubmitByEnter = (
    event: React.KeyboardEvent<HTMLFormElement>,
  ) => {
    if (event.key === 'Enter') event.preventDefault();
  };

  useEffect(() => {
    const updateSchemaDescribe = () => {
      setSchemaDescription((prevState) => {
        const newDescription = getSchemaDescription(form.getValues());

        return isEqual(newDescription, prevState) ? prevState : newDescription;
      });
    };

    updateSchemaDescribe();
    const { unsubscribe } = form.watch(updateSchemaDescribe);

    return () => {
      unsubscribe();
    };
  }, [form, getSchemaDescription]);

  return (
    <SchemaProvider schema={schemaDescription}>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          onKeyDown={submitByEnter ? undefined : preventSubmitByEnter}
          autoComplete="off"
          className={className}
        >
          {children}
        </form>
      </FormProvider>
    </SchemaProvider>
  );
};

Form.InputNumber = InputNumber;
Form.InputText = InputText;
Form.InputTextarea = InputTextarea;
Form.Checkbox = Checkbox;
Form.Dropdown = Dropdown;
Form.Calendar = Calendar;
Form.ColorPicker = ColorPicker;
Form.MultiSelect = MultiSelect;

Form.ErrorMessage = ErrorMessage;

const makeForm = <T extends AnyRecord>(): FormType<T> => Form;

export default makeForm;
